import {ChangeDetectionStrategy, Component, inject, input, OnInit, signal, WritableSignal} from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {EntityAccountViewModel} from '../view-models/entity-account.view-model';
import {EntityAccountService} from '../services/entity-account.service';
import {SortedTable} from '@shared/enums/sorted-table.enum';
import {TableTemplateColumnConfig} from '@components/ui/table-template/models/table-template-column-config';
import {CellType} from '@components/ui/table-template/enums/cell-type.enum';
import {Alignment} from '@components/ui/table-template/enums/alignment.enum';
import {EntityAccountTransactionViewModel} from '../view-models/entity-account-transaction.view-model';
import {EntityAccountTransactionConverterService} from '../services/entity-account-transaction-converter.service';
import {Keys} from '@shared/keys';
import {ClientsideDataService} from '@shared/services/clientside-data.service';
import {BaseEntityDetailsModal} from '../../shared/base-entity-details-modal';
import {EntityTransactionModalComponent} from '../../shared/entity-transaction-modal/entity-transaction-modal.component';

@Component({
    selector: 'app-entity-account-details-modal',
    templateUrl: './entity-account-details-modal.component.html',
    styleUrls: ['./entity-account-details-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ClientsideDataService],
    imports: [EntityTransactionModalComponent]
})
export class EntityAccountDetailsModalComponent
  extends BaseEntityDetailsModal<EntityAccountViewModel, EntityAccountTransactionViewModel>
  implements OnInit {

  readonly modalDataInput = input.required<EntityAccountViewModel>();

  readonly entityAccount: WritableSignal<EntityAccountViewModel> = signal(undefined as unknown as EntityAccountViewModel);

  private readonly entityAccountService = inject(EntityAccountService);
  private readonly entityAccountTransactionConverterService = inject(EntityAccountTransactionConverterService);

  ngOnInit() {
    this.entityAccount.set(this.modalDataInput());
    this.initializeModal(this.entityAccount(), 'EntityAccountDetailsModal');
  }

  protected populateColumns(): TableTemplateColumnConfig[] {
    return [
      {
        title: 'Date',
        dataProperty: 'date',
        cellType: CellType.Date,
        sortable: true,
        alignment: Alignment.Left,
        width: '27%',
        dataFocusable: true,
        headingFocusable: true
      },
      {
        title: 'Description',
        dataProperty: 'description',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Left,
        width: '48%',
        dataFocusable: false,
        headingFocusable: true
      },
      {
        title: 'Amount',
        dataProperty: 'amount',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Right,
        width: '25%',
        dataFocusable: false,
        headingFocusable: true
      }
    ];
  }

  protected getSearchFields(): Keys<EntityAccountTransactionViewModel> {
    return ['description'];
  }

  protected getSortedTable(): SortedTable {
    return SortedTable.Accounts;
  }

  protected getTransactionSortedTable(): SortedTable {
    return SortedTable.AccountTransactions;
  }

  protected loadTransactions(entity: EntityAccountViewModel): Observable<EntityAccountTransactionViewModel[]> {
    return this.entityAccountService.getEntityAccountTransactions(entity.entityCode, entity.id).pipe(
      map(transactionList => this.entityAccountTransactionConverterService.convert(transactionList))
    );
  }
}
